<template>
  <div class="page-container">
    <!-- 头部 -->
    <Header />

    <!-- 主要内容 -->
    <div class="solutions-container">
      <!-- 标题区域 -->
      <div 
        class="page-header"
        :style="{
          backgroundImage: isImageLoaded && currentSolution.cover ? `url(${currentSolution.cover})` : 'linear-gradient(135deg, #0B1B48, #172B4D, #1E3A8A)',
          opacity: 1
        }"
      >
        <!-- 添加一个背景过渡层 -->
        <div class="background-transition" 
          :style="{ 
            backgroundImage: `linear-gradient(135deg, #0B1B48, #172B4D, #1E3A8A)`,
            opacity: isImageLoaded ? 0 : 1 
          }"
        ></div>
        
        <div class="container">
          <h1>{{ currentSolution.name || solutionTabs[currentTab].title }}</h1>
          <p class="description">{{ currentSolution.desc || solutionTabs[currentTab].subtitle }}</p>
        </div>
      </div>

      <!-- 导航区域 -->
      <div class="solutions-nav">
        <div class="container">
          <div 
            v-for="(item, index) in solutionTabs" 
            :key="index"
            class="nav-item"
            :class="{ 'active': currentTab === index }"
            @click="changeTab(index)"
          >
            {{ item.shortTitle || item.title }}
          </div>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="solutions-content">
        <component :is="currentComponent" />
      </div>
    </div>

    <!-- 底部 -->
    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { columnGet2 } from "@/api/court";
import Header from '@/layout/header/index.vue'
import Footer from '@/layout/footer/index.vue'
import Operation from './components/Operation.vue'
import Hardware from './components/Hardware.vue'
import Data from './components/Data.vue'
import Enterprise from './components/Enterprise.vue'

const route = useRoute()
const router = useRouter()
import { getBaseApiUrl } from '@/utils/api-config'
const baseUrl = getBaseApiUrl()

const currentSolution = ref({})
const controller = ref(null) // 用于存储 AbortController
const isImageLoaded = ref(false)

// 预加载图片函数
const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No image URL'))
      return
    }
    const img = new Image()
    img.src = url.startsWith('http') ? url : `${baseUrl}/media${url}`
    img.onload = () => {
      isImageLoaded.value = true
      resolve()
    }
    img.onerror = () => {
      isImageLoaded.value = false
      reject(new Error('Image load failed'))
    }
  })
}

// 获取页面数据
const getPageData = async (tabName) => {
  try {
    isImageLoaded.value = false
    
    if (controller.value) {
      controller.value.abort()
    }
    
    controller.value = new AbortController()

    const response = await columnGet2({
      page: 1,
      limit: 10000,
      is_enable: true,
      parent: 2
    }, {
      signal: controller.value.signal
    });

    if (response.data.code === 2000) {
      const data = response.data.data.data.find(item => {
        return item.name.includes(tabName) || item.name.toLowerCase().includes(tabName.toLowerCase())
      })

      if (data) {

        currentSolution.value = data
        // 如果有背景图，预加载它
        if (data.cover) {
          try {
            await preloadImage(data.cover)
          } catch (error) {
            console.error('背景图加载失败:', error)
          }
        }
      }
    }
  } catch (error) {
    if (error.name === 'AbortError' || error.message === 'Request aborted') {
      return
    }
    console.error('获取页面数据失败:', error)
  }
}

// 添加防抖函数
const debounce = (fn, delay) => {
  let timer = null
  return (...args) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

// 使用防抖包装 getPageData
const debouncedGetPageData = debounce(getPageData, 300)

// 解决方案选项卡
const solutionTabs = [
  { 
    title: '运维服务解决方案', 
    shortTitle: '运维服务',
    subtitle: '专业的IT运维服务体系，为您的业务保驾护航',
    component: Operation, 
    path: '/solutions/operation',
    color: '#2b5dff',
    key: '运行维护'
  },
  { 
    title: '硬件维保服务方案', 
    shortTitle: '硬件维保',
    subtitle: '专业的硬件维护保障服务，延长设备使用寿命',
    component: Hardware, 
    path: '/solutions/hardware',
    color: '#1976d2',
    key: '硬件维保'
  },
  { 
    title: '数据治理服务方案', 
    shortTitle: '数据治理',
    subtitle: '专业的数据治理服务，助力企业数字化转型',
    component: Data, 
    path: '/solutions/data',
    color: '#0d47a1',
    key: '数据治理'
  },
  { 
    title: '企业数字化服务解决方案', 
    shortTitle: '企业数字化',
    subtitle: '全方位的企业信息化服务，助力企业数字化转型',
    component: Enterprise, 
    path: '/solutions/enterprise',
    color: '#283593',
    key: '企业数字化'
  }
]

const currentTab = ref(0)
const currentComponent = computed(() => solutionTabs[currentTab.value].component)

// 监听tab变化
watch(currentTab, (newVal) => {
  debouncedGetPageData(solutionTabs[newVal].key)
})

// 组件卸载时取消请求
onUnmounted(() => {
  if (controller.value) {
    controller.value.abort()
  }
})

// 根据当前路由设置活动选项卡
onMounted(() => {
  // 确保页面从顶部开始显示
  console.log('🔧 解决方案页面组件挂载，确保滚动到顶部')
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
  window.scrollTo(0, 0)

  const path = route.path
  const tabIndex = solutionTabs.findIndex(tab => path.includes(tab.path))
  if (tabIndex !== -1) {
    currentTab.value = tabIndex
    debouncedGetPageData(solutionTabs[tabIndex].key)
  }
})

// 切换选项卡并更新路由
const changeTab = (index) => {
  currentTab.value = index
  router.push(solutionTabs[index].path)
}
</script>

<style lang="scss" scoped>
.page-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.solutions-container {
  flex: 1;
  background: #f8f9fc;
  margin-top: 60px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-header {
  background-size: cover;
  background-position: center;
  color: #fff;
  padding: 60px 0 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-bottom:0px;
  transition: all 0.5s ease;

  .background-transition {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: opacity 0.5s ease;
    z-index: 0;
  }

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0.5;
    z-index: 1;
  }

  .container {
    position: relative;
    z-index: 2;
  }

  h1 {
    font-size: 36px;
    margin-bottom: 10px;
    position: relative;
    display: inline-block;
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.8s ease forwards;
  }

  .description {
    font-size: 18px;
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.8s ease forwards 0.2s;
    margin: 20px auto 0;
    max-width: 800px;
    line-height: 1.8;
  }
}

.solutions-nav {
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 62px;
  z-index: 10;
  
  .container {
    display: flex;
    justify-content: center;
  }
  
  .nav-item {
    padding: 20px 30px;
    font-size: 16px;
    color: #666;
    cursor: pointer;
    position: relative;
    transition: all 0.3s;
    
    &:hover {
      color: #2b5dff;
    }
    
    &.active {
      color: #2b5dff;
      font-weight: 500;
      
      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 3px;
        background: #2b5dff;
        animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
    }
  }
}

.solutions-content {
  padding: 60px 0;
}

@keyframes slideIn {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .solutions-container {
    margin-top: 60px; /* 移动端顶部导航高度调整 */
  }
  
  .solutions-header {
    padding: 40px 0;
    
    h1 {
      font-size: 28px;
    }
    
    p {
      font-size: 16px;
    }
  }
  
  .solutions-nav {
    top: 60px;
    
    .container {
      flex-wrap: wrap;
    }
    
    .nav-item {
      padding: 15px 20px;
      font-size: 14px;
      flex: 1 0 50%;
      text-align: center;
    }
  }
  
  .solutions-content {
    padding: 40px 0;
  }
}
</style> 